import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setCurrentUser } from "../../redux/userRedux";
import { ref, update } from "firebase/database";
import { database } from "../../firebase";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import useIsMobile from "../../hooks/useIsMobile";
import { updateUserSpecificCases } from "../../utils/updateUserSpecificCases";

const CARD = {
  backgroundColor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: "12px",
  padding: "24px",
  marginTop: "16px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
};

const LABEL_STYLE = { color: "#475569", fontWeight: 600, fontSize: "15px" };
const VALUE_STYLE = { color: "#0F172A", fontSize: "15px" };
const INPUT_STYLE = {
  width: "100%",
  backgroundColor: "#F8FAFC",
  border: "1px solid #E2E8F0",
  borderRadius: "8px",
  padding: "10px 14px",
  color: "#0F172A",
  fontSize: "15px",
  outline: "none",
};
const TEAL_BTN = {
  backgroundColor: "#30A69C",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "8px 28px",
  fontWeight: 600,
  cursor: "pointer",
  fontSize: "15px",
};
const GHOST_BTN = {
  backgroundColor: "#F1F5F9",
  color: "#475569",
  border: "1px solid #E2E8F0",
  borderRadius: "8px",
  padding: "8px 28px",
  fontWeight: 600,
  cursor: "pointer",
  fontSize: "15px",
};

const ProfilePageView = () => {
  const currentUser = useSelector((state) => state.user.currentUser);
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState({
    firstName: currentUser?.firstName || "",
    lastName: currentUser?.lastName || "",
    email: currentUser?.email || "",
    number: currentUser?.number || "",
    department: currentUser?.department || "",
    institutionName: currentUser?.institutionName || "",
  });
  const [originalData, setOriginalData] = useState(formData);
  const [success, setSuccess] = useState(null);
  const [emailList, setEmailList] = useState(currentUser?.emailList || []);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    if (currentUser?.emailList) setEmailList(currentUser.emailList);
  }, [currentUser]);

  const fieldLabels = {
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    number: "Phone Number",
    department: "Department",
    institutionName: "Institution Name",
  };

  const formatName = (name) =>
    name.split(" ").map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: formatName(value) });
  };
  const handlePhoneChange = (value) => setFormData({ ...formData, number: value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const oldFullName = `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim();
    const newFullName = `${formData.firstName} ${formData.lastName}`.trim();
    dispatch(setCurrentUser(formData));
    setIsEditing(false);
    const userRef = ref(database, `users/${currentUser.id}`);
    await update(userRef, formData);
    if (oldFullName !== newFullName && newFullName !== "") {
      await updateUserSpecificCases(currentUser, newFullName);
    }
    setSuccess("Profile updated successfully.");
  };

  const handleCancel = () => { setFormData(originalData); setIsEditing(false); };
  const handleEdit = () => { setOriginalData(formData); setIsEditing(true); };
  const handleKeyDown = (e) => { if (e.key === "Enter") handleSubmit(e); };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";
    const parsed = parsePhoneNumberFromString(phoneNumber);
    return parsed ? parsed.formatInternational() : phoneNumber;
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleAddEmail = async () => {
    const trimmedEmail = newEmail.trim();
    if (!trimmedEmail) { setEmailError("Please enter an email address."); return; }
    if (!validateEmail(trimmedEmail)) { setEmailError("Please enter a valid email address."); return; }
    if (emailList.includes(trimmedEmail)) { setEmailError("This email is already in the list."); return; }
    const updated = [...emailList, trimmedEmail];
    setEmailList(updated); setNewEmail(""); setEmailError("");
    if (currentUser?.id) {
      const userRef = ref(database, `users/${currentUser.id}`);
      await update(userRef, { emailList: updated });
      dispatch(setCurrentUser({ ...currentUser, emailList: updated }));
    }
  };

  const handleRemoveEmail = async (emailToRemove) => {
    const updated = emailList.filter((e) => e !== emailToRemove);
    setEmailList(updated);
    if (currentUser?.id) {
      const userRef = ref(database, `users/${currentUser.id}`);
      await update(userRef, { emailList: updated });
      dispatch(setCurrentUser({ ...currentUser, emailList: updated }));
    }
  };

  const phoneInputDarkStyle = {
    width: "100%",
    backgroundColor: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: "8px",
    padding: "10px 14px 10px 50px",
    color: "#E7EBEF",
    fontSize: "15px",
  };

  return (
    <div
      className={isMobile ? "p-4" : "p-8"}
      style={{ minHeight: "100vh", backgroundColor: "#0B0E13" }}
    >
      <h2 style={{ fontSize: "22px", fontWeight: 800, color: "#FFFFFF" }}>Profile</h2>

      {success && (
        <div style={{ color: "#4ADE80", marginBottom: "8px", fontSize: "14px" }}>{success}</div>
      )}

      {isEditing ? (
        <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
          <div style={CARD}>
            {isMobile ? (
              <>
                {Object.keys(formData).map((key) => (
                  <div key={key} style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", fontWeight: 600, color: "#475569", marginBottom: "6px", fontSize: "13px" }}>
                      {fieldLabels[key]}
                    </label>
                    {key === "number" ? (
                      <PhoneInput
                        value={formData[key]}
                        onChange={handlePhoneChange}
                        inputStyle={{ ...phoneInputDarkStyle }}
                        placeholder="Select your country and enter your phone number"
                      />
                    ) : key === "email" ? (
                      <p style={{ ...INPUT_STYLE, wordBreak: "break-all" }}>{formData[key]}</p>
                    ) : (
                      <input type="text" name={key} value={formData[key]} onChange={handleChange} style={INPUT_STYLE} />
                    )}
                  </div>
                ))}
                <div className="flex space-x-2">
                  <button type="submit" style={{ ...TEAL_BTN, flex: 1 }}>Save</button>
                  <button type="button" onClick={handleCancel} style={{ ...GHOST_BTN, flex: 1 }}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", fontWeight: 600, color: "#475569", marginBottom: "8px", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Full Name</label>
                  <div className="flex gap-4">
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First name" required style={{ ...INPUT_STYLE, flex: 1 }} />
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last name" required style={{ ...INPUT_STYLE, flex: 1 }} />
                  </div>
                </div>
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", fontWeight: 600, color: "#475569", marginBottom: "8px", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Phone Number</label>
                  <PhoneInput
                    value={formData.number}
                    onChange={handlePhoneChange}
                    inputStyle={{ ...phoneInputDarkStyle }}
                    placeholder="Select your country and enter your phone number"
                    inputProps={{ id: "number", name: "number", required: true }}
                  />
                </div>
                {["department", "institutionName"].map((key) => (
                  <div key={key} style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", fontWeight: 600, color: "#475569", marginBottom: "8px", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.05em" }}>{fieldLabels[key]}</label>
                    <input type="text" name={key} value={formData[key]} onChange={handleChange} placeholder={fieldLabels[key]} style={INPUT_STYLE} />
                  </div>
                ))}
                <div className="flex space-x-2" style={{ marginTop: "20px" }}>
                  <button type="submit" style={TEAL_BTN}>Save</button>
                  <button type="button" onClick={handleCancel} style={GHOST_BTN}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </form>
      ) : (
        <div style={CARD}>
          <table className="table-auto w-full">
            <tbody>
              <tr style={{ borderBottom: "1px solid #F1F5F9" }}>
                <td className="px-2 py-3 w-1/3" style={LABEL_STYLE}>Full name</td>
                <td className="px-2 py-3 w-2/3" style={VALUE_STYLE}>
                  {(formData.firstName || "") + " " + (formData.lastName || "")}
                </td>
              </tr>
              {Object.keys(formData)
                .filter((key) => key !== "firstName" && key !== "lastName")
                .map((key) => (
                  <tr key={key} style={{ borderBottom: "1px solid #F1F5F9" }}>
                    <td className="px-2 py-3 w-1/3" style={LABEL_STYLE}>{fieldLabels[key]}</td>
                    <td className="px-2 py-3 w-2/3" style={VALUE_STYLE}>
                      {key === "number" ? formatPhoneNumber(`+${formData[key]}`) : formData[key]}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <button
            onClick={handleEdit}
            style={{ ...TEAL_BTN, marginTop: "20px" }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#228880"}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#30A69C"}
          >
            Edit
          </button>
        </div>
      )}

      {/* Email List Section */}
      <div style={{ marginTop: "32px" }}>
        <h2 style={{ fontSize: "22px", fontWeight: 800, color: "#FFFFFF", marginBottom: "16px" }}>Email List</h2>
        <div style={CARD}>
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontWeight: 600, color: "#475569", marginBottom: "8px", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Add Email</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => { setNewEmail(e.target.value); if (emailError) setEmailError(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddEmail(); } }}
                placeholder="Enter email address"
                style={{ ...INPUT_STYLE, flex: 1 }}
              />
              <button
                type="button"
                onClick={handleAddEmail}
                style={{ ...TEAL_BTN, whiteSpace: "nowrap" }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#228880"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#30A69C"}
              >
                Add
              </button>
            </div>
            {emailError && <p style={{ color: "#F87171", fontSize: "13px", marginTop: "6px" }}>{emailError}</p>}
          </div>

          {emailList.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontWeight: 600, color: "#475569", marginBottom: "8px", fontSize: "13px", textTransform: "uppercase" }}>
                Email List ({emailList.length})
              </label>
              <div style={{ border: "1px solid #E2E8F0", borderRadius: "8px", maxHeight: "240px", overflowY: "auto" }}>
                <ul>
                  {emailList.map((email, index) => (
                    <li
                      key={index}
                      className="px-4 py-3 flex justify-between items-center"
                      style={{ borderBottom: index < emailList.length - 1 ? "1px solid #F1F5F9" : "none" }}
                    >
                      <span style={{ color: "#475569", wordBreak: "break-all", flex: 1, fontSize: "14px" }}>{email}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(email)}
                        style={{ marginLeft: "16px", color: "#F87171", fontWeight: 600, fontSize: "13px", background: "none", border: "none", cursor: "pointer" }}
                        onMouseEnter={(e) => e.currentTarget.style.color = "#EF4444"}
                        onMouseLeave={(e) => e.currentTarget.style.color = "#F87171"}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {emailList.length === 0 && (
            <p style={{ color: "#64748B", fontSize: "14px", fontStyle: "italic" }}>
              No emails added yet. Add emails above to get started.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePageView;
